<template>
    <Layout
        :no-header="isEmbed"
        :title="title"
        :content-class="['page-editor', 'page-suggestions']"
    >
        <template #header-center>
            <router-link
                v-tooltip="{
                    content: $t('editor.goToSettings'),
                    delay: { show: 500 },
                    distance: 15,
                }"
                :to="{ name: 'book-settings', params: { id: $route.params.id } }"
                class="editor-page__settings-link"
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M15.9501 7.11169C15.9248 6.88678 15.6626 6.71777 15.4358 6.71777C14.7027 6.71777 14.0522 6.2873 13.7793 5.62156C13.5006 4.93968 13.6803 4.14446 14.2267 3.64326C14.3987 3.48604 14.4196 3.22285 14.2753 3.04003C13.9001 2.56354 13.4736 2.13313 13.008 1.7601C12.8256 1.61377 12.5581 1.63413 12.4 1.80921C11.9231 2.33732 11.0666 2.53359 10.4047 2.25743C9.71594 1.96769 9.28159 1.26974 9.3241 0.520533C9.33809 0.285204 9.1661 0.0805941 8.93149 0.0532691C8.33391 -0.0158471 7.73109 -0.0179902 7.13173 0.0485066C6.8998 0.0741052 6.72781 0.273893 6.73561 0.506245C6.76168 1.24807 6.3221 1.93381 5.64017 2.21314C4.98621 2.48025 4.13568 2.28565 3.65979 1.76224C3.50251 1.58984 3.23932 1.56865 3.05608 1.71146C2.57661 2.08764 2.14054 2.51841 1.76186 2.99098C1.61417 3.17469 1.6359 3.44098 1.80961 3.59903C2.36641 4.10326 2.54619 4.90539 2.25693 5.59513C1.98076 6.25271 1.29793 6.67652 0.516226 6.67652C0.262562 6.66836 0.0818834 6.83862 0.0541417 7.06925C-0.0162246 7.67028 -0.0170581 8.28268 0.0506889 8.88859C0.0758112 9.11446 0.346025 9.28198 0.575281 9.28198C1.27192 9.26418 1.94076 9.69549 2.22133 10.3783C2.50101 11.0601 2.32122 11.8549 1.77401 12.3565C1.60286 12.5137 1.58113 12.7765 1.72537 12.9593C2.09709 13.4328 2.52363 13.8636 2.99095 14.2397C3.17425 14.3874 3.44095 14.3666 3.59984 14.1915C4.07848 13.6621 4.93496 13.4662 5.59421 13.7429C6.28471 14.0317 6.71906 14.7296 6.67655 15.4792C6.66268 15.7147 6.8355 15.9196 7.06916 15.9465C7.37486 15.9822 7.6824 16 7.99077 16C8.28349 16 8.57626 15.9839 8.86898 15.9514C9.10097 15.9258 9.27284 15.726 9.26504 15.4932C9.23819 14.7518 9.67855 14.0661 10.3596 13.7872C11.018 13.5183 11.8649 13.7151 12.3409 14.238C12.499 14.41 12.7604 14.4308 12.9446 14.2885C13.4232 13.9132 13.8584 13.4828 14.2388 13.0089C14.3865 12.8256 14.3656 12.5589 14.191 12.4009C13.6342 11.8967 13.4536 11.0944 13.7428 10.4052C14.0148 9.75633 14.6722 9.32067 15.3794 9.32067L15.4784 9.32323C15.7077 9.34187 15.9188 9.16518 15.9466 8.93104C16.0171 8.32947 16.0179 7.71761 15.9501 7.11169ZM8.01309 10.6854C6.54159 10.6854 5.34465 9.48843 5.34465 8.01693C5.34465 6.54549 6.54159 5.34849 8.01309 5.34849C9.48454 5.34849 10.6815 6.54549 10.6815 8.01693C10.6815 9.48843 9.48454 10.6854 8.01309 10.6854Z"
                        fill="#A6A7BE"
                    />
                </svg>
            </router-link>
        </template>
        <template #header-after-support>
            <portal-target name="header-after-support" multiple />
        </template>
        <CRMOrderStateControlWrapper v-slot="{ orderStateControlVisible }">
            <EditorSelector v-slot="{ selectedBookpart }" @select="onBookpartSelect">
                <div
                    ref="editor"
                    class="editor"
                    :class="{
                        'editor--embed': isEmbed,
                        'editor--fullheight': isEmbed && orderStateControlVisible,
                    }"
                >
                    <Spinner v-if="loading || !book.id" />
                    <template v-if="book.id">
                        <div class="editor__text text">
                            <div class="page-suggestions__toolbar">
                                <router-link
                                    class="button button--gray"
                                    :to="`/${editorType}/${book.id}`"
                                >
                                    ‹ {{ $t('export.repair.backToBookEditing') }}
                                </router-link>
                                <CustomSelect
                                    v-model="filterValue"
                                    view-label="label"
                                    :options="filterOptions"
                                    compact
                                    @update:model-value="onFilterUpdate"
                                />
                            </div>
                            <div class="text__inner">
                                <div class="text__container">
                                    <PaginatedResource
                                        ref="partsRes"
                                        :filter-fields="filter"
                                        :fetch-method="fetchSuggestions"
                                    >
                                        <template
                                            #default="{
                                                loading,
                                                canLoadMore,
                                                resource: parts,
                                                isEmpty,
                                                notFound,
                                                loadMore,
                                            }"
                                        >
                                            <div class="page-suggestions__results">
                                                <Spinner v-if="loading" />
                                                <BookpartsList
                                                    :loading="loading"
                                                    :parts="parts"
                                                    no-data-text="editor.suggestion.noData"
                                                    bookpart-key="book_part_v2"
                                                />
                                                <div
                                                    v-if="canLoadMore"
                                                    class="page-suggestions__loadmore"
                                                >
                                                    <button
                                                        v-t="'misc.loadmore'"
                                                        class="button button--big button--blank"
                                                        type="button"
                                                        @click="loadMore"
                                                    />
                                                </div>
                                            </div>
                                        </template>
                                    </PaginatedResource>
                                </div>
                            </div>
                        </div>

                        <button
                            v-if="selectedBookpart"
                            class="editor__toolbar__toggle"
                            :class="['suggestion', { close: showToolbar }]"
                            @click="toggleToolbar"
                        >
                            {{ showToolbar ? '&gt;' : '&lt;' }}
                        </button>

                        <EditorToolbar :part="selectedBookpart" :class="{ show: showToolbar }" />
                    </template>
                </div>
            </EditorSelector>
        </CRMOrderStateControlWrapper>

        <portal v-if="activeSuggestion" to="toolbar">
            <div class="toolbar__group">
                <h4 v-t="'editor.toolbar.suggestion'" class="toolbar__heading" />
            </div>
            <div class="toolbar__controls">
                <div class="editor-form__row">
                    <span v-t="'editor.suggestion.isCompleted'" />
                    <Switcher
                        v-model="activeSuggestion.is_completed"
                        @update:model-value="updateSuggestion"
                    />
                </div>
                <div class="editor-form__row">
                    <span v-t="'editor.suggestion.errorFound'" />
                    <Switcher
                        v-model="activeSuggestion.error_found"
                        @update:model-value="updateSuggestion"
                    />
                </div>
            </div>
        </portal>
    </Layout>
</template>

<script>
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Switcher from '@speechki-frontend/shared/ui/Switcher';
import { CustomSelect } from '@speechki-frontend/shared/ui/forms';
import { BookpartsList, EditorToolbar } from '@speechki-frontend/core/editor/components';
import { mapState, mapGetters, mapActions } from 'vuex';
import { fetchSuggestions, updateSuggestion } from '../../api/suggestions';
import CRMOrderStateControlWrapper from '../../components/CRMOrderStateControl/CRMOrderStateControlWrapper';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import EditorSelector from '../../components/EditorSelector/EditorSelector';
import { useSwipe } from '@vueuse/core';

export default {
    name: 'SuggestionsPage',
    components: {
        Layout,
        Switcher,
        CustomSelect,
        BookpartsList,
        EditorToolbar,
        CRMOrderStateControlWrapper,
        PaginatedResource,
        EditorSelector,
    },
    provide() {
        return {
            isEmbed: this.isEmbed,
        };
    },
    props: {
        isEmbed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const filterOptions = [
            {
                value: null,
                label: this.$t('editor.suggestion.filter.all'),
            },
            {
                value: true,
                label: this.$t('editor.suggestion.filter.isCompleted'),
            },
            {
                value: false,
                label: this.$t('editor.suggestion.filter.unCompleted'),
            },
        ];

        return {
            loading: false,
            activeSuggestion: null,
            filterOptions,
            filterValue: filterOptions[2],

            swipe: null,
            isForceShow: false,
        };
    },
    computed: {
        ...mapState({
            book: ({ book }) => book.book,
        }),
        ...mapGetters({
            title: 'bookTitle',
        }),
        editorType() {
            return this.$route.path.includes('embed') ? 'embed' : 'editor';
        },
        filter() {
            const { params } = this.$route;
            return {
                book: params.id,
                is_completed: this.filterValue.value,
            };
        },
        isSwiping() {
            if (this.swipe === null) {
                return false;
            }
            return this.swipe.isSwiping;
        },
        showToolbar() {
            return this.isForceShow;
        },
    },
    watch: {
        title: {
            immediate: true,
            handler() {
                document.title = this.$t('routes.editor', { title: this.title });
            },
        },
        suggestions() {
            this.activeSuggestion = null;
        },
        isSwiping() {
            if (this.isSwiping) {
                return;
            }

            if (Math.abs(this.swipe.lengthX) > 2 * Math.abs(this.swipe.lengthY)) {
                let direction = this.swipe.direction;

                if (direction === 'right' && this.isForceShow) {
                    this.isForceShow = false;
                }

                if (
                    direction === 'left' &&
                    !this.isForceShow &&
                    this.$refs.editor.__vue__.selectedBookpart !== null
                ) {
                    this.isForceShow = true;
                }
            }
        },
    },
    async created() {
        const { params } = this.$route;

        this.loading = true;
        if (this.book && parseInt(params.id) !== this.book.id) {
            this.clearBook();
        }

        await Promise.all([this.fetchBook(params.id)]);

        this.loading = false;
    },
    beforeUnmount() {
        document.body.classList.remove('editor-page');
        this.clearBook();
    },
    mounted() {
        document.body.classList.add('editor-page');

        this.swipe = useSwipe(this.$refs.editor, {
            threshold: 100,
        });
    },
    methods: {
        ...mapActions(['fetchBook', 'clearBook', 'pushToast']),
        fetchSuggestions,

        async updateSuggestion() {
            try {
                const { id, is_completed, error_found } = this.activeSuggestion;
                const data = { is_completed, error_found };
                await updateSuggestion(id, data);

                this.pushToast({
                    type: 'success',
                    title: 'Updated',
                });
            } catch (err) {
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                });
                console.log(err);
            }
        },
        onFilterUpdate() {
            this.$refs.partsRes.filter(this.filter);
        },
        async onBookpartSelect([selected]) {
            if (!selected) {
                this.activeSuggestion = null;
            }

            const {
                results: [suggestion],
            } = await fetchSuggestions({
                book_part_v2: selected.bookpart.id,
                page_size: 1,
                ordering: '-id',
            });

            this.activeSuggestion = suggestion;
        },

        toggleToolbar() {
            this.isForceShow = !this.isForceShow;
        },
    },
};
</script>

<style lang="scss" src="./SuggestionsPage.scss"></style>
